import { toast } from "react-toastify";
import { DynamicField, DynamicFieldTypes } from "src/components/dynamic-widget";
import DynamicFormWidget from "src/components/dynamic-widget/DynamicFormWidget";
import { DataService } from "src/services/data-service";
import { useState, useEffect } from "react";
import GenericDrawer from "src/components/drawer/GenericDrawer";
import { useParams } from "react-router-dom";

interface IEditJobProps {
  onSuccess: () => void;
  onClose: () => void;
  id: string;
}

const EditJob: React.FC<IEditJobProps> = ({ onClose, onSuccess, id }) => {
  const [submitting, setSubmitting] = useState(false);

  const [formConfig, setFormConfig] = useState<Record<string, DynamicFieldTypes>>({
    Title: {
      type: DynamicField.TEXT,
      required: true,
      name: "Title",
      title: "Job Name",
      value: "",
      disabled: submitting,
    },
    Type: {
      type: DynamicField.TEXT,
      required: true,
      name: "Type",
      title: "Job Type",
      value: "",
      disabled: submitting,
    },
    Location: {
      type: DynamicField.TEXT,
      required: true,
      name: "Location",
      title: "Job Location",
      value: "",
      disabled: submitting,
    },
    Description: {
      type: DynamicField.TEXTAREA,
      required: true,
      name: "Description",
      title: "Job Description",
      value: "",
      disabled: submitting,
    },
  });

  // Fetch the job data by id and update the form values.
  useEffect(() => {
    const fetchJob = async () => {
      try {
        const response = await DataService.get(`api/job/get?id=${id}`);
        if (!response.ok) {
          toast.error("Failed to load job data");
          return;
        }
        const jobData = await response.json();
        setFormConfig((prev) => ({
          ...prev,
          Title: { ...prev.Title, value: jobData.title || "" },
          Type: { ...prev.Type, value: jobData.type || "" },
          Location: { ...prev.Location, value: jobData.location || "" },
          Description: { ...prev.Description, value: jobData.description || "" },
        }));
      } catch (error) {
        toast.error("Error fetching job data");
      }
    };
    if (id) {
      fetchJob();
    }
  }, [id]);

  const submit = async (data: Record<string, any>) => {
    setSubmitting(true);
    try {
      const response = await DataService.post(`api/job/update/${id}`, data);
      if (!response.ok) {
        const result: { message: string } = await response.json();
        throw new Error(result.message || "An error occurred while updating the job!");
      }
      toast.success("Job updated successfully!");
      await onSuccess();
      onClose();
      return true;
    } catch (error) {
      toast.error(error.message || "Failed to update job!");
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <GenericDrawer title={"Edit Job"} open={true} onClose={onClose}>
      <DynamicFormWidget drawerMode title="" fields={formConfig} onSubmit={submit} />
    </GenericDrawer>
  );
};

export default EditJob;
